import React from 'react'

import { Box, Tooltip, useTheme } from '@mui/material'

import { Button, MenuButton, TOOLBAR_CONTROL_HEIGHT } from '@repo/ui'
import { Download, FileText, Table2 } from 'lucide-react'
// Direct file import, not the `components` barrel — this file is part of that barrel.
import { toolbarLabelSx, toolbarStartIconSx } from '../ActionLine/toolbarCollapse'

export type ExportFormat = 'csv' | 'xlsx'

interface ActivityDownloadProps {
  onExport: (format: ExportFormat) => void
  disabled?: boolean
}

/**
 * Activity-log export control. Reuses the SAME dropdown as Sort (the shared
 * `@repo/ui` `Select`) — just `search={false}` so there's no
 * search bar — so spacing, radius, hover, and elevation match exactly. The
 * trigger is a tertiary `Button` in a contained `ButtonGroup` (matches Filter/Sort).
 */
/*
  The trigger is LABELLED and sits at the toolbar's height.

  It was an icon-only button wrapped in a single-child `ButtonGroup variant="contained"`, which
  produced two visible defects beside its Filter/Sort neighbours: the word "Download" was
  missing where every other surface in the app carries it, and the group's own padding/border
  made it a different HEIGHT from the buttons it sits next to.

  `TOOLBAR_CONTROL_HEIGHT` (49) rather than a size prop — §29: the table toolbar band is 49
  because it shares a row with a 49px search FIELD, and that parity is the whole reason the
  number differs from the page header's 40.

  A ButtonGroup around ONE button was never buying anything; it only imported the group's chrome.
*/
export const ActivityDownload = ({ onExport, disabled = false }: ActivityDownloadProps) => {
  const theme = useTheme()

  return (
  <MenuButton
    search={false}
    menuMinWidth={200}
    onSelect={(item: { value: ExportFormat }) => onExport(item.value)}
    items={[
      { label: 'CSV', value: 'csv', icon: <FileText size={16} strokeWidth={1.5} /> },
      { label: 'Excel (.xlsx)', value: 'xlsx', icon: <Table2 size={16} strokeWidth={1.5} /> },
    ]}
    button={
      <Tooltip title="Download" enterDelay={600}>
      <Button
        // `secondary`, matching Filter and Sort beside it. `tertiary` was the remaining
        // mismatch: it paints a darker fill (rgb(24,24,27) vs rgb(31,31,35)) and a border the
        // same colour as that fill, so the button read as borderless next to two bordered
        // neighbours. Height/radius/padding/font already agreed — this was the last difference.
        variant="secondary"
        disabled={disabled}
        // The accessible name — the visible label is `display: none` at a narrow container,
        // which removes it from the accessibility tree as well as the layout.
        aria-label="Download"
        // No `size` — Filter/Sort let the lucide default (24) stand. An 18 here was visibly
        // smaller than the icons either side of it.
        startIcon={<Download color={theme.palette.primary[800]} strokeWidth={1} />}
        sx={{
          height: TOOLBAR_CONTROL_HEIGHT,
          flexShrink: 0,
          // Zeroes `startIcon`'s margins while the label is hidden so the glyph centres.
          ...toolbarStartIconSx('download'),
        }}
      >
        {/* The LABEL collapses exactly as Filter and Sort do — which is now a CONTAINER query
            on the toolbar row, not the viewport `md` this used to mirror (VRG-535,
            `toolbarCollapse.ts`). It shares the `download` rung with the Table toolbar's own
            export button: same role, same position in the row, so it must drop at the same
            width or the two Download buttons in this app would disagree.

            Following the peers is the whole point of this control's styling, so it has to
            follow them HERE too. Left on `md` it would have become the one button in the row
            still answering to the viewport — the mirror image of the inconsistency the
            comment this replaced was written to fix. */}
        <Box component="span" sx={toolbarLabelSx('download')}>
          Download
        </Box>
      </Button>
      </Tooltip>
    }
  />
)
}
